import { Children, useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import styles from './EnergyOverviewCarousel.module.scss'
import SliderArrow from '../arrow-button/ArrowButton.tsx'
import { EnergyOverviewCarouselProps } from './types.ts'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { trackEvent } from 'utils/tracking.ts'

const EnergyOverviewCarousel = ({ children, triggerConfetti, id }: EnergyOverviewCarouselProps) => {
  // Local state
  const [currentSlide, setCurrentSlide] = useState<number>(0)

  // Window size
  const { isMobile } = useWindowSize()

  // Slides are rendered conditionally by the sections, so the falsy ones are dropped to keep the slide count accurate
  const slides = useMemo(() => Children.toArray(children), [children])
  const slideCount = slides.length

  // Carousel
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', loop: false, slidesToScroll: 1, watchDrag: isMobile })

  useEffect(() => {
    if (!emblaApi) return

    const handleSelect = () => {
      const nextSlide = emblaApi.selectedScrollSnap()
      trackEvent({ category: 'energy_overview', object: 'go_to_slide', action: 'click' }, { slide: nextSlide, id })
      setCurrentSlide(nextSlide)
    }

    const handleSettle = () => {
      if (!triggerConfetti) return

      const settledSlide = emblaApi.selectedScrollSnap()

      if (settledSlide + 1 === slideCount) {
        triggerConfetti(true)
      } else if (settledSlide === slideCount - 2) {
        triggerConfetti(false)
      }
    }

    emblaApi.on('select', handleSelect).on('settle', handleSettle)

    return () => {
      emblaApi.off('select', handleSelect).off('settle', handleSettle)
    }
  }, [emblaApi, id, slideCount, triggerConfetti])

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  return (
    <div className={styles.carousel}>
      <div ref={emblaRef} className={styles.viewport}>
        <ul className={styles.container}>
          {slides.map((slide, index) => (
            <li key={index} className={styles.slide}>
              {slide}
            </li>
          ))}
        </ul>
      </div>

      {!isMobile && (
        <>
          <SliderArrow action="previous" onClick={scrollPrev} disabled={currentSlide === 0} />
          <SliderArrow action="next" onClick={scrollNext} disabled={currentSlide + 1 === slideCount} />
        </>
      )}

      <nav>
        <ul className={styles.dots}>
          {slides.map((_, index) => (
            <li key={index}>
              <button
                type="button"
                className={classNames(styles.dot, { [styles.active]: currentSlide === index })}
                aria-label={`slide ${index + 1}`}
                onClick={() => emblaApi?.scrollTo(index)}
              />
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}

export default EnergyOverviewCarousel
